import {
  cloneState,
  type EntityStatus,
  type EntityType,
  type FieldType,
  type FrameworkId,
  type RelationPatch,
  type SchemaField,
  type SyncrelChatMessage,
  type SyncrelRelation,
  type SyncrelState,
} from "../domain/types";
import { FRAMEWORK_CANVAS_FIELD } from "../domain/framework-scope";

export interface EntityPatch {
  name?: string;
  type?: EntityType;
  status?: EntityStatus;
  doc?: string;
  fields?: Record<string, unknown>;
}

export interface EntityInput {
  id?: string;
  type?: EntityType;
  name?: string;
  status?: EntityStatus;
  fields?: Record<string, unknown>;
  doc?: string;
  position?: { x: number; y: number };
}

export interface RelationInput {
  fromId: string;
  toId: string;
  type: string;
  label: string;
}

export interface SchemaFieldInput {
  label: string;
  type: FieldType;
  key?: string;
  options?: string[];
  defaultValue?: unknown;
  required?: boolean;
  readonly?: boolean;
  description?: string;
}

export interface ChatMessageInput {
  author: string;
  text: string;
}

const CHAT_MESSAGE_LIMIT = 100;

export const selectEntity = (state: SyncrelState, entityId: string | null, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  next.workspace.selectedEntityId = entityId;
  next.workspace.selectedRelationId = null;
  next.workspace.updatedAt = now;
  return next;
};

export const selectRelation = (state: SyncrelState, relationId: string | null, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  const exists = relationId ? next.relations.some((relation) => relation.id === relationId) : false;
  next.workspace.selectedRelationId = exists ? relationId : null;
  if (exists) next.workspace.selectedEntityId = null;
  next.workspace.updatedAt = now;
  return next;
};

export const renameWorkspace = (
  state: SyncrelState,
  name: string,
  now = Date.now(),
): SyncrelState => {
  const cleanName = name.trim().replace(/\s+/g, " ");
  if (!cleanName) return state;
  const next = cloneState(state);
  next.workspace.name = cleanName;
  next.workspace.updatedAt = now;
  return next;
};

export const addEntity = (state: SyncrelState, input: EntityInput = {}, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  const name = input.name?.trim() || "New feature";
  const type = input.type ?? "feature";
  const id = uniqueEntityId(next, input.id ?? `${type}-${slugify(name)}-${now}`);
  const entity = {
    id,
    type,
    name,
    status: input.status ?? "planned",
    fields: {
      ...(next.workspace.activeFramework ? { [FRAMEWORK_CANVAS_FIELD]: next.workspace.activeFramework } : {}),
      ...(input.fields ?? {}),
    },
    doc: input.doc ?? `# ${name}\n\n## Problem\n\n## Scope\n`,
    position: {
      x: Math.max(8, input.position?.x ?? 220),
      y: Math.max(8, input.position?.y ?? 160),
    },
    createdAt: now,
    updatedAt: now,
  };
  next.entities = [...next.entities, entity];
  next.workspace.selectedEntityId = id;
  next.workspace.selectedRelationId = null;
  next.workspace.updatedAt = now;
  return next;
};

export const removeEntity = (state: SyncrelState, entityId: string, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  const removedRelationIds = new Set(
    next.relations
      .filter((relation) => relation.fromId === entityId || relation.toId === entityId)
      .map((relation) => relation.id),
  );
  next.entities = next.entities.filter((entity) => entity.id !== entityId);
  next.relations = next.relations.filter((relation) => !removedRelationIds.has(relation.id));
  if (next.workspace.selectedEntityId === entityId) {
    next.workspace.selectedEntityId = next.entities[0]?.id ?? null;
  }
  next.workspace.selectedRelationId = next.workspace.selectedRelationId &&
    !removedRelationIds.has(next.workspace.selectedRelationId)
    ? next.workspace.selectedRelationId
    : null;
  next.workspace.updatedAt = now;
  return next;
};

export const patchEntity = (
  state: SyncrelState,
  entityId: string,
  patch: EntityPatch,
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  next.entities = next.entities.map((entity) => {
    if (entity.id !== entityId) return entity;
    return {
      ...entity,
      name: patch.name ?? entity.name,
      type: patch.type ?? entity.type,
      status: patch.status ?? entity.status,
      doc: patch.doc ?? entity.doc,
      fields: {
        ...entity.fields,
        ...(patch.fields ?? {}),
      },
      updatedAt: now,
    };
  });
  next.workspace.updatedAt = now;
  return next;
};

export const addSchemaField = (
  state: SyncrelState,
  input: SchemaFieldInput,
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  const label = input.label.trim();
  if (!label) return next;

  const key = uniqueFieldKey(next, input.key?.trim() || slugify(label));
  const options = input.options?.map((option) => option.trim()).filter(Boolean);
  const defaultValue = normalizeDefaultValue(input.type, input.defaultValue, options);
  const field: SchemaField = {
    key,
    label,
    type: input.type,
    ...(options && options.length > 0 ? { options } : {}),
    ...(defaultValue !== undefined ? { defaultValue } : {}),
    ...(input.required ? { required: true } : {}),
    ...(input.readonly || input.type === "computed" ? { readonly: true } : {}),
    ...(input.description?.trim() ? { description: input.description.trim() } : {}),
  };

  next.schema.fields = [...next.schema.fields.filter((item) => item.key !== key), field];
  next.entities = next.entities.map((entity) => ({
    ...entity,
    fields: {
      ...entity.fields,
      ...(defaultValue !== undefined && entity.fields[key] === undefined ? { [key]: defaultValue } : {}),
    },
    updatedAt: defaultValue !== undefined && entity.fields[key] === undefined ? now : entity.updatedAt,
  }));
  next.workspace.updatedAt = now;
  return next;
};

export const markFrameworkWelcomeSeen = (
  state: SyncrelState,
  frameworkId: FrameworkId,
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  next.workspace.frameworkWelcomeSeen = Array.from(new Set([
    ...(next.workspace.frameworkWelcomeSeen ?? []),
    frameworkId,
  ]));
  next.workspace.updatedAt = now;
  return next;
};

export const moveEntity = (
  state: SyncrelState,
  entityId: string,
  position: { x: number; y: number },
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  next.entities = next.entities.map((entity) => (
    entity.id === entityId
      ? { ...entity, position: { x: position.x, y: position.y }, updatedAt: now }
      : entity
  ));
  next.workspace.updatedAt = now;
  return next;
};

export const linkEntities = (state: SyncrelState, input: RelationInput, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  const id = `rel-${input.fromId}-${input.toId}-${input.type}`;
  const relation: SyncrelRelation = {
    id,
    fromId: input.fromId,
    toId: input.toId,
    type: input.type,
    label: input.label,
    createdAt: now,
  };
  next.relations = [
    ...next.relations.filter((item) => item.id !== id),
    relation,
  ];
  next.workspace.updatedAt = now;
  return next;
};

export const patchRelation = (
  state: SyncrelState,
  relationId: string,
  patch: RelationPatch,
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  next.relations = next.relations.map((relation) => {
    if (relation.id !== relationId) return relation;
    return {
      ...relation,
      type: patch.type ?? relation.type,
      label: patch.label ?? relation.label,
    };
  });
  next.workspace.updatedAt = now;
  return next;
};

export const removeRelation = (state: SyncrelState, relationId: string, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  next.relations = next.relations.filter((relation) => relation.id !== relationId);
  if (next.workspace.selectedRelationId === relationId) next.workspace.selectedRelationId = null;
  next.workspace.updatedAt = now;
  return next;
};

export const addSpecPoll = (state: SyncrelState, entityId: string, now = Date.now()): SyncrelState => {
  const next = cloneState(state);
  next.entities = next.entities.map((entity) => {
    if (entity.id !== entityId) return entity;
    const existing = entity.polls ?? [];
    const pollId = uniqueSpecPollId(existing.map((poll) => poll.id), now);
    return {
      ...entity,
      polls: [
        ...existing,
        {
          id: pollId,
          question: "What should readers vote on?",
          options: [
            { id: `${pollId}-option-1`, label: "Option 1" },
            { id: `${pollId}-option-2`, label: "Option 2" },
          ],
          votes: [],
          createdAt: now,
          updatedAt: now,
        },
      ],
      updatedAt: now,
    };
  });
  next.workspace.updatedAt = now;
  return next;
};

export const patchSpecPollQuestion = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  question: string,
  now = Date.now(),
): SyncrelState => updateSpecPoll(state, entityId, pollId, now, (poll) => ({
  ...poll,
  question,
  updatedAt: now,
}));

export const patchSpecPollOption = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  optionId: string,
  label: string,
  now = Date.now(),
): SyncrelState => updateSpecPoll(state, entityId, pollId, now, (poll) => ({
  ...poll,
  options: poll.options.map((option) => (
    option.id === optionId ? { ...option, label } : option
  )),
  updatedAt: now,
}));

export const addSpecPollOption = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  now = Date.now(),
): SyncrelState => updateSpecPoll(state, entityId, pollId, now, (poll) => {
  const nextIndex = poll.options.length + 1;
  return {
    ...poll,
    options: [
      ...poll.options,
      {
        id: uniqueSpecPollOptionId(poll.options.map((option) => option.id), pollId, nextIndex),
        label: `Option ${nextIndex}`,
      },
    ],
    updatedAt: now,
  };
});

export const removeSpecPollOption = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  optionId: string,
  now = Date.now(),
): SyncrelState => updateSpecPoll(state, entityId, pollId, now, (poll) => {
  if (poll.options.length <= 2) return poll;
  return {
    ...poll,
    options: poll.options.filter((option) => option.id !== optionId),
    votes: poll.votes.filter((vote) => vote.optionId !== optionId),
    updatedAt: now,
  };
});

export const removeSpecPoll = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  now = Date.now(),
): SyncrelState => {
  const next = cloneState(state);
  next.entities = next.entities.map((entity) => (
    entity.id === entityId
      ? {
        ...entity,
        polls: (entity.polls ?? []).filter((poll) => poll.id !== pollId),
        updatedAt: now,
      }
      : entity
  ));
  next.workspace.updatedAt = now;
  return next;
};

export const voteSpecPoll = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  optionId: string,
  viewerId: string,
  now = Date.now(),
): SyncrelState => updateSpecPoll(state, entityId, pollId, now, (poll) => {
  if (!poll.options.some((option) => option.id === optionId)) return poll;
  const safeViewerId = viewerId.trim();
  if (!safeViewerId) return poll;
  return {
    ...poll,
    votes: [
      ...poll.votes.filter((vote) => vote.viewerId !== safeViewerId),
      { viewerId: safeViewerId, optionId, votedAt: now },
    ],
    updatedAt: now,
  };
});

export const addChatMessage = (
  state: SyncrelState,
  input: ChatMessageInput,
  now = Date.now(),
): SyncrelState => {
  const text = input.text.trim();
  if (!text) return state;
  const next = cloneState(state);
  const message: SyncrelChatMessage = {
    id: `chat-${now}`,
    author: input.author.trim() || "Guest",
    text,
    createdAt: now,
  };
  next.chat = [...(next.chat ?? []), message].slice(-CHAT_MESSAGE_LIMIT);
  next.workspace.updatedAt = now;
  return next;
};

const updateSpecPoll = (
  state: SyncrelState,
  entityId: string,
  pollId: string,
  now: number,
  update: (poll: NonNullable<SyncrelState["entities"][number]["polls"]>[number]) => NonNullable<SyncrelState["entities"][number]["polls"]>[number],
): SyncrelState => {
  const next = cloneState(state);
  let changed = false;
  next.entities = next.entities.map((entity) => {
    if (entity.id !== entityId) return entity;
    const polls = entity.polls ?? [];
    const nextPolls = polls.map((poll) => {
      if (poll.id !== pollId) return poll;
      changed = true;
      return update(poll);
    });
    return changed ? { ...entity, polls: nextPolls, updatedAt: now } : entity;
  });
  if (changed) next.workspace.updatedAt = now;
  return next;
};

const slugify = (value: string): string => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "entity";
};

const uniqueEntityId = (state: SyncrelState, seed: string): string => {
  const used = new Set(state.entities.map((entity) => entity.id));
  if (!used.has(seed)) return seed;
  let index = 2;
  while (used.has(`${seed}-${index}`)) index += 1;
  return `${seed}-${index}`;
};

const uniqueSpecPollId = (usedIds: string[], now: number): string => {
  const used = new Set(usedIds);
  const seed = `poll-${now}`;
  if (!used.has(seed)) return seed;
  let index = 2;
  while (used.has(`${seed}-${index}`)) index += 1;
  return `${seed}-${index}`;
};

const uniqueSpecPollOptionId = (usedIds: string[], pollId: string, seedIndex: number): string => {
  const used = new Set(usedIds);
  const seed = `${pollId}-option-${seedIndex}`;
  if (!used.has(seed)) return seed;
  let index = seedIndex + 1;
  while (used.has(`${pollId}-option-${index}`)) index += 1;
  return `${pollId}-option-${index}`;
};

const uniqueFieldKey = (state: SyncrelState, seed: string): string => {
  const normalized = slugify(seed).replaceAll("-", "_");
  const base = normalized || "field";
  const used = new Set([
    "name",
    "status",
    "type",
    ...state.schema.fields.map((field) => field.key),
  ]);
  if (!used.has(base)) return base;
  let index = 2;
  while (used.has(`${base}_${index}`)) index += 1;
  return `${base}_${index}`;
};

const normalizeDefaultValue = (
  type: FieldType,
  value: unknown,
  options?: string[],
): unknown => {
  if (value === undefined || value === null || value === "") {
    if (type === "boolean") return false;
    return undefined;
  }
  if (type === "number" || type === "currency" || type === "percent") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (type === "boolean") {
    return value === true || value === "true" || value === "on";
  }
  if (type === "select") {
    const selected = String(value);
    return options?.includes(selected) ? selected : options?.[0] ?? selected;
  }
  if (type === "multi-select") {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    return String(value).split(",").map((item) => item.trim()).filter(Boolean);
  }
  return String(value);
};
